import { useMemo } from "react";

import type { MeetingModelJson } from "@performyard/service-contracts";

import { useInternalApiUser } from "../../../../api/hooks";

type UseManagerDirectReportMeetingParams = {
	meeting: MeetingModelJson | null | undefined;
	userId: string;
	isAdmin: boolean;
	isParticipant: boolean;
};

export const useManagerDirectReportMeeting = ({
	meeting,
	userId,
	isAdmin,
	isParticipant,
}: UseManagerDirectReportMeetingParams): boolean => {
	const isAdminNonParticipant = isAdmin && !isParticipant;
	const meetingParticipantIds = useMemo(
		() => meeting?.participants?.map((participant) => participant.userId).filter(Boolean) || [],
		[meeting?.participants]
	);
	const isOneToOneMeeting = meetingParticipantIds.length === 2;
	const otherParticipantId = meetingParticipantIds.find((participantId) => participantId !== userId);

	const { data: currentUserInfo } = useInternalApiUser(userId, {
		enabled: isOneToOneMeeting && Boolean(userId),
	});
	const { data: otherParticipantInfo } = useInternalApiUser(otherParticipantId || "", {
		enabled: isOneToOneMeeting && Boolean(otherParticipantId),
	});

	return useMemo(() => {
		if (isAdminNonParticipant || !isOneToOneMeeting || !otherParticipantId) {
			return false;
		}

		const currentUserDirectReports = currentUserInfo?.directReports || [];
		const otherUserDirectReports = otherParticipantInfo?.directReports || [];

		const isCurrentUserManager = currentUserDirectReports.some((report) => report._id === otherParticipantId);
		const isOtherUserManager = otherUserDirectReports.some((report) => report._id === userId);

		return isCurrentUserManager || isOtherUserManager;
	}, [
		currentUserInfo?.directReports,
		isAdminNonParticipant,
		isOneToOneMeeting,
		otherParticipantId,
		otherParticipantInfo?.directReports,
		userId,
	]);
};
